import { createSlice } from '@reduxjs/toolkit';

import {
  ConsumablesOrder,
  ConsumablesProduct,
  ConsumablesProductCategory,
  ConsumablesProductSubscription,
  ConsumablesProductType,
  VendorFee
} from 'shared';

const initialState: ConsumablesState = {
  selectedCategoryName: null,
  selectedTypeName: null,
  searchQuery: '',
  categories: [],
  types: [],
  products: [],
  featuredTypes: [],
  consumablesStateLoaded: false,
  consumablesProductSubscriptions: null,
  consumablesOrders: null,
  deliveryFees: null,
  showOnboardingCartToast: false,
  cartToastZIndex: -1,
  hideToastTimeoutId: null,
  resetZIndexTimeoutId: null,
  filteredConsumablesProducts: null
};

export type ConsumablesState = {
  selectedCategoryName: string | null;
  selectedTypeName: string | null;
  searchQuery: string;
  categories: ConsumablesProductCategory[];
  types: ConsumablesProductType[];
  products: ConsumablesProduct[];
  featuredTypes: ConsumablesProductType[];
  consumablesStateLoaded: boolean;
  consumablesProductSubscriptions: ConsumablesProductSubscription[] | null;
  consumablesOrders: ConsumablesOrder[] | null;
  deliveryFees: VendorFee[] | null;
  showOnboardingCartToast: boolean;
  cartToastZIndex: number;
  hideToastTimeoutId: NodeJS.Timeout | null;
  resetZIndexTimeoutId: NodeJS.Timeout | null;
  filteredConsumablesProducts: ConsumablesProduct[] | null;
};

const consumablesSlice = createSlice({
  name: 'Consumables',
  initialState,
  reducers: {
    setSelectedCategoryName: (state, action) => {
      state.selectedCategoryName = action.payload;
    },
    setSelectedTypeName: (state, action) => {
      state.selectedTypeName = action.payload;
    },
    setSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },
    setProducts: (state, action) => {
      state.products = action.payload;
    },
    setCategories: (state, action) => {
      state.categories = action.payload;
    },
    setTypes: (state, action) => {
      state.types = action.payload;
    },

    setConsumablesStateLoaded: (state, action) => {
      state.consumablesStateLoaded = action.payload;
    },

    removeConsumablesProductSubscription: (state, action) => {
      if (state.consumablesProductSubscriptions) {
        state.consumablesProductSubscriptions = state.consumablesProductSubscriptions.filter(
          (productSubscription: ConsumablesProductSubscription) => productSubscription.id !== action.payload
        );
      }
    },

    setConsumablesProductSubscriptions: (state, action) => {
      state.consumablesProductSubscriptions = action.payload;
    },

    setConsumablesOrders: (state, action) => {
      state.consumablesOrders = action.payload;
    },

    updateConsumablesOrder: (state, action) => {
      if (!state.consumablesOrders) return;
      state.consumablesOrders = state.consumablesOrders?.map(order =>
        order.id === action.payload.id ? { ...order, has_given_feedback: action.payload.has_given_feedback } : order
      );
    },
    setDeliveryFees: (state, action) => {
      state.deliveryFees = action.payload;
    },
    setShowOnboardingCartToast: (state, action) => {
      state.showOnboardingCartToast = action.payload;
    },
    setCartToastZIndex: (state, action) => {
      state.cartToastZIndex = action.payload;
    },
    setHideToastTimeoutId: (state, action) => {
      state.hideToastTimeoutId = action.payload;
    },
    setResetZIndexTimeoutId: (state, action) => {
      state.resetZIndexTimeoutId = action.payload;
    },
    setFilteredConsumablesProducts: (state, action) => {
      state.filteredConsumablesProducts = action.payload;
    }
  }
});

export const { actions } = consumablesSlice;

export default consumablesSlice.reducer;
